import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import clsx from 'clsx';
import {
  fetchPublicStoreInfo,
  lookupPublicCustomer,
  submitPublicCustomerInfo,
  submitPublicForm,
  submitPublicWaiverSign,
  type LoyaltyLookupResult,
  type PublicStoreInfo,
} from '@/lib/api/public-display.js';
import {
  createReconnectingWebSocket,
  type WsConnectionStatus,
} from '@/lib/ws-reconnect.js';
import { SignaturePad } from '@/components/common/SignaturePad.js';
import { buildFormSignatureValue, isSignatureFieldEmpty } from '@/lib/forms/signature.js';

type DisplayDeviceGroup = {
  label: string;
  services: Array<{ id?: string; name: string; price: string }>;
  passcode?: string | null;
  condition?: string | null;
};

function DeviceMetaUnderTitle({
  condition,
  passcode,
  showCondition,
  showPin,
}: {
  condition?: string | null;
  passcode?: string | null;
  showCondition: boolean;
  showPin: boolean;
}) {
  const [revealed, setRevealed] = useState(false);
  if (!showCondition && !showPin) return null;
  return (
    <div className="mt-1 space-y-0.5 text-sm text-slate-300">
      {showCondition && condition ? <p>Condition: {condition}</p> : null}
      {showPin && passcode ? (
        <button
          type="button"
          className="text-left text-amber-100 underline-offset-2 hover:underline"
          onClick={() => setRevealed((v) => !v)}
        >
          PIN: {revealed ? passcode : '••••••'} {revealed ? '(hide)' : '(tap to reveal)'}
        </button>
      ) : null}
    </div>
  );
}

function shouldShowTaxLine(
  taxTotal: unknown,
  displayShowTaxLine: boolean | undefined,
): boolean {
  if (displayShowTaxLine === false) return false;
  return Math.abs(Number(taxTotal ?? 0)) > 0.009;
}

type DisplayMode =
  | 'idle'
  | 'customer_info'
  | 'review'
  | 'waiver'
  | 'form'
  | 'checkout'
  | 'payment_processing'
  | 'thank_you';

type DisplayFormField = {
  id: string;
  type: string;
  label: string;
  required?: boolean;
  options?: string[];
  helpText?: string;
  order: number;
};

type RequiredFields = {
  firstName: boolean;
  phone: boolean;
  lastName: boolean;
  email: boolean;
  address: boolean;
  cityStateZip: boolean;
};

const defaultRequired: RequiredFields = {
  firstName: true,
  phone: true,
  lastName: false,
  email: false,
  address: false,
  cityStateZip: false,
};

function formatMoneyFromPoints(points: number): string {
  return (points / 100).toFixed(2);
}

function formatCurrency(n: string | number) {
  const v = Number(n);
  if (Number.isNaN(v)) return '$0.00';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(v);
}

export function DisplayPage() {
  const { storeId } = useParams<{ storeId: string }>();
  const [storeInfo, setStoreInfo] = useState<PublicStoreInfo | null>(null);
  const [mode, setMode] = useState<DisplayMode>('idle');
  const [lastPayload, setLastPayload] = useState<Record<string, unknown> | null>(null);
  const [customerSession, setCustomerSession] = useState<{
    sessionToken: string;
    requiredFields: RequiredFields;
    idleMessage: string | null;
  } | null>(null);
  const [loyaltyBanner, setLoyaltyBanner] = useState<string | null>(null);
  const [phoneDigits, setPhoneDigits] = useState('');
  const [lookupLoading, setLookupLoading] = useState(false);
  const [customerForm, setCustomerForm] = useState({
    firstName: '',
    lastName: '',
    phone: '',
    email: '',
    address: '',
    city: '',
    state: '',
    zip: '',
  });
  const [waiverScrollOk, setWaiverScrollOk] = useState(false);
  const [hasSignature, setHasSignature] = useState(false);
  const [wsStatus, setWsStatus] = useState<WsConnectionStatus>('connecting');
  const [formValues, setFormValues] = useState<Record<string, unknown>>({});
  const [formSubmitting, setFormSubmitting] = useState(false);
  const waiverScrollRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const drawing = useRef(false);
  const lastPt = useRef<{ x: number; y: number } | null>(null);
  const hasInk = useRef(false);

  const brandColor = '#1B4F8A';

  useEffect(() => {
    if (!storeId) return;
    void fetchPublicStoreInfo(storeId)
      .then(setStoreInfo)
      .catch(() => setStoreInfo(null));
  }, [storeId]);

  useEffect(() => {
    if (!loyaltyBanner) return;
    const t = window.setTimeout(() => setLoyaltyBanner(null), 15_000);
    return () => window.clearTimeout(t);
  }, [loyaltyBanner]);

  useEffect(() => {
    if (!storeId) return;

    let thankYouTimer: ReturnType<typeof setTimeout> | undefined;

    function handleWsMessage(msg: { type?: string; payload?: Record<string, unknown> }) {
      if (msg.type === 'ping') return;
      const p = msg.payload ?? {};
      setLastPayload(p as Record<string, unknown>);

      switch (msg.type) {
        case 'display.customer_info_request':
          setCustomerSession({
            sessionToken: String(p.sessionToken ?? ''),
            requiredFields: { ...defaultRequired, ...(p.requiredFields as Partial<RequiredFields>) },
            idleMessage: (p.idleMessage as string | null) ?? null,
          });
          setCustomerForm({
            firstName: '',
            lastName: '',
            phone: '',
            email: '',
            address: '',
            city: '',
            state: '',
            zip: '',
          });
          setMode('customer_info');
          break;
        case 'display.update':
          if (p.mode === 'idle') {
            setMode('idle');
            setLastPayload(null);
            break;
          }
          if (p.mode === 'review') setMode('review');
          break;
        case 'display.waiver_request':
          setWaiverScrollOk(false);
          setHasSignature(false);
          setMode('waiver');
          break;
        case 'display.form_request':
          setFormValues({});
          setFormSubmitting(false);
          setMode('form');
          break;
        case 'display.checkout':
          setMode('checkout');
          break;
        case 'display.payment_processing':
          setMode('payment_processing');
          break;
        case 'display.thank_you': {
          setMode('thank_you');
          if (thankYouTimer !== undefined) window.clearTimeout(thankYouTimer);
          thankYouTimer = window.setTimeout(() => {
            setMode('idle');
            setLastPayload(null);
          }, 10_000);
          break;
        }
        default:
          break;
      }
    }

    const socket = createReconnectingWebSocket({
      path: `/ws/display/${storeId}`,
      onStatus: setWsStatus,
      onMessage: (event) => {
        try {
          handleWsMessage(
            JSON.parse(event.data as string) as { type?: string; payload?: Record<string, unknown> },
          );
        } catch {
          /* ignore */
        }
      },
    });

    return () => {
      if (thankYouTimer !== undefined) window.clearTimeout(thankYouTimer);
      socket.close();
    };
  }, [storeId]);

  const idleHeadline = storeInfo?.idleMessage?.trim() || "We'll be right with you!";

  const onLoyaltyLookup = async () => {
    if (!storeId || phoneDigits.replace(/\D/g, '').length < 10) return;
    setLookupLoading(true);
    try {
      const res = await lookupPublicCustomer(phoneDigits, storeId);
      if (!(res as LoyaltyLookupResult).found) {
        setLoyaltyBanner('No account found for that number. Ask staff for help.');
      } else {
        const r = res as Extract<LoyaltyLookupResult, { found: true }>;
        const pts = Math.floor(Number(r.loyaltyPointsBalance ?? 0));
        const dollars = formatMoneyFromPoints(pts);
        setLoyaltyBanner(`Hi ${r.firstName}! You have ${pts} points ≈ $${dollars} in rewards.`);
      }
    } catch {
      setLoyaltyBanner('Lookup failed. Try again in a moment.');
    } finally {
      setLookupLoading(false);
    }
  };

  const required = customerSession?.requiredFields ?? defaultRequired;

  const submitCustomerInfo = async () => {
    if (!storeId || !customerSession?.sessionToken) return;
    if (required.firstName && !customerForm.firstName.trim()) return;
    if (required.phone && !customerForm.phone.trim()) return;
    if (required.lastName && !customerForm.lastName.trim()) return;
    if (required.email && !customerForm.email.trim()) return;
    if (required.address && !customerForm.address.trim()) return;
    if (
      required.cityStateZip &&
      (!customerForm.city.trim() || !customerForm.state.trim() || !customerForm.zip.trim())
    ) {
      return;
    }
    try {
      await submitPublicCustomerInfo({
        storeId,
        sessionToken: customerSession.sessionToken,
        customerInfo: {
          firstName: customerForm.firstName.trim(),
          lastName: customerForm.lastName.trim() || undefined,
          phone: customerForm.phone.trim(),
          email: customerForm.email.trim() || undefined,
          address: customerForm.address.trim() || undefined,
          city: customerForm.city.trim() || undefined,
          state: customerForm.state.trim() || undefined,
          zip: customerForm.zip.trim() || undefined,
        },
      });
      setCustomerSession(null);
      setMode('idle');
      setLastPayload({ thankYouNote: 'Thank you! Please hand the iPad back to staff.' });
      window.setTimeout(() => setLastPayload(null), 5000);
    } catch {
      setLastPayload({ thankYouNote: 'Something went wrong. Ask staff to resend the form.' });
      window.setTimeout(() => setLastPayload(null), 5000);
    }
  };

  const checkWaiverScroll = useCallback(() => {
    const el = waiverScrollRef.current;
    if (!el) return;
    const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 24;
    setWaiverScrollOk(nearBottom);
  }, []);

  useEffect(() => {
    if (mode !== 'waiver') return;
    const id = window.requestAnimationFrame(() => checkWaiverScroll());
    return () => window.cancelAnimationFrame(id);
  }, [mode, checkWaiverScroll, lastPayload]);

  const pos = useCallback((e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) return null;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    if ('touches' in e && e.touches[0]) {
      return {
        x: (e.touches[0].clientX - rect.left) * scaleX,
        y: (e.touches[0].clientY - rect.top) * scaleY,
      };
    }
    if ('clientX' in e) {
      return { x: (e.clientX - rect.left) * scaleX, y: (e.clientY - rect.top) * scaleY };
    }
    return null;
  }, []);

  const startDraw = useCallback(
    (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
      const ctx = canvasRef.current?.getContext('2d');
      const p = pos(e);
      if (!ctx || !p) return;
      drawing.current = true;
      lastPt.current = p;
      ctx.strokeStyle = '#0f172a';
      ctx.lineWidth = 2.5;
      ctx.lineCap = 'round';
    },
    [pos],
  );

  const draw = useCallback(
    (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
      if (!drawing.current) return;
      const canvas = canvasRef.current;
      const ctx = canvas?.getContext('2d');
      const p = pos(e);
      if (!ctx || !p || !lastPt.current) return;
      ctx.beginPath();
      ctx.moveTo(lastPt.current.x, lastPt.current.y);
      ctx.lineTo(p.x, p.y);
      ctx.stroke();
      hasInk.current = true;
      setHasSignature(true);
      lastPt.current = p;
    },
    [pos],
  );

  const endDraw = useCallback(() => {
    drawing.current = false;
    lastPt.current = null;
  }, []);

  const clearSignature = useCallback(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;
    ctx.fillStyle = '#fff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    hasInk.current = false;
    setHasSignature(false);
  }, []);

  useEffect(() => {
    if (mode !== 'waiver') return;
    requestAnimationFrame(() => {
      const canvas = canvasRef.current;
      const ctx = canvas?.getContext('2d');
      if (canvas && ctx) {
        ctx.fillStyle = '#fff';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        hasInk.current = false;
        setHasSignature(false);
      }
    });
  }, [mode, lastPayload]);

  const submitWaiver = async () => {
    const p = lastPayload;
    if (!p || !storeId) return;
    const displayToken = String(p.displayToken ?? '');
    const waiverVersion = String(p.waiverVersion ?? '');
    const waiverType = p.waiverType as 'pre_repair' | 'post_repair';
    const ticketId = String(p.ticketId ?? '');
    const canvas = canvasRef.current;
    if (!canvas || !waiverScrollOk || !hasSignature) return;
    const signatureDataUrl = canvas.toDataURL('image/png');
    try {
      await submitPublicWaiverSign(ticketId, {
        displayToken,
        signatureDataUrl,
        waiverType,
        waiverVersion,
      });
      setMode('idle');
      setLastPayload({ thankYouNote: '✓ Signed! Thank you.' });
      window.setTimeout(() => setLastPayload(null), 3000);
    } catch {
      setLastPayload({ thankYouNote: 'Could not submit signature. Ask staff to resend the waiver.' });
      window.setTimeout(() => setLastPayload(null), 5000);
    }
  };

  const submitDisplayForm = async () => {
    const p = lastPayload;
    if (!p) return;
    const displayToken = String(p.displayToken ?? '');
    const fields = ((p.fields as DisplayFormField[]) ?? [])
      .slice()
      .sort((a, b) => a.order - b.order);

    const values: Record<string, unknown> = { ...formValues };

    for (const field of fields) {
      if (!field.required || field.type === 'heading' || field.type === 'divider') continue;
      if (field.type === 'signature') {
        if (isSignatureFieldEmpty(values[field.id])) {
          setLastPayload({
            ...p,
            formError: `Please sign "${field.label}"`,
          });
          return;
        }
        continue;
      }
      const v = values[field.id];
      if (v === undefined || v === null || v === '') {
        setLastPayload({
          ...p,
          formError: `"${field.label}" is required`,
        });
        return;
      }
    }

    setFormSubmitting(true);
    try {
      await submitPublicForm({ displayToken, fieldValues: values });
      setMode('idle');
      setLastPayload({ thankYouNote: '✓ Form submitted. Thank you!' });
      window.setTimeout(() => setLastPayload(null), 3000);
    } catch {
      setLastPayload({
        ...p,
        formError: 'Could not submit form. Ask staff to send it again.',
      });
    } finally {
      setFormSubmitting(false);
    }
  };

  const review = useMemo(() => {
    if (!lastPayload || mode !== 'review') return null;
    return lastPayload;
  }, [lastPayload, mode]);

  type DisplayServiceLine = { id?: string; name: string; price: string };

  const services = (review?.services as DisplayServiceLine[] | undefined) ?? [];
  const items = (review?.items as { description: string; lineTotal: string }[] | undefined) ?? [];
  const isPosCart = Boolean(review?.isPosCart);
  const isTicketDraft = Boolean(review?.isTicketDraft);

  const deviceGroups: DisplayDeviceGroup[] = useMemo(() => {
    if (!review || isPosCart) return [];
    const raw = review.deviceGroups as DisplayDeviceGroup[] | undefined;
    if (Array.isArray(raw) && raw.length > 0) return raw;
    if (services.length === 0) return [];
    const devs = (review.devices as string[] | undefined) ?? [];
    return [
      {
        label: devs[0] ?? 'Services',
        services,
        passcode: (review.passcode as string | null | undefined) ?? null,
        condition: (review.condition as string | null | undefined) ?? null,
      },
    ];
  }, [review, isPosCart, services]);

  const showTax =
    review != null &&
    shouldShowTaxLine(review.taxTotal, storeInfo?.displayShowTaxLine);
  const showDevicePin = Boolean(storeInfo?.displayShowDevicePin);
  const showDeviceCondition = Boolean(storeInfo?.displayShowDeviceCondition);
  const showPerDeviceSections = deviceGroups.length > 1;

  type PosLine = {
    id?: string;
    description: string;
    quantity?: number;
    unitPrice?: number;
    lineTotal?: string | number;
    discountAmount?: number;
  };

  const posLineItems: PosLine[] = useMemo(() => {
    if (!isPosCart || !review) return [];
    const raw = review.items as PosLine[] | undefined;
    return Array.isArray(raw) ? raw : [];
  }, [isPosCart, review]);

  const prevPosLineIdsRef = useRef<Set<string>>(new Set());
  const [posLineEnterIds, setPosLineEnterIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    if ((!isPosCart && !isTicketDraft) || mode !== 'review') {
      prevPosLineIdsRef.current = new Set();
      return;
    }
    const ids = isPosCart
      ? posLineItems.map((it, i) => String(it.id ?? `${it.description}-${i}`))
      : deviceGroups.flatMap((g, gi) =>
          g.services.map((s, si) => String(s.id ?? `${gi}-${s.name}-${si}`)),
        );
    const next = new Set(ids);
    const newly = new Set(ids.filter((id) => !prevPosLineIdsRef.current.has(id)));
    prevPosLineIdsRef.current = next;
    if (newly.size === 0) return;
    setPosLineEnterIds(newly);
    const t = window.setTimeout(() => setPosLineEnterIds(new Set()), 450);
    return () => window.clearTimeout(t);
  }, [isPosCart, isTicketDraft, mode, posLineItems, deviceGroups]);

  if (!storeId) {
    return <div className="flex min-h-screen items-center justify-center bg-slate-950 text-white">Invalid display URL.</div>;
  }

  return (
    <main
      className="min-h-screen text-white"
      style={{
        background: `linear-gradient(165deg, ${brandColor} 0%, #0f172a 45%, #020617 100%)`,
      }}
    >
      <div className="pointer-events-none fixed bottom-4 right-4 z-50 flex items-center gap-2 rounded-full border border-white/20 bg-black/30 px-3 py-1.5 text-xs font-medium text-slate-200 backdrop-blur">
        <span
          className={clsx(
            'h-2 w-2 rounded-full',
            wsStatus === 'open' && 'bg-emerald-400',
            wsStatus === 'failed' && 'bg-red-400',
            wsStatus !== 'open' && wsStatus !== 'failed' && 'animate-pulse bg-amber-400',
          )}
        />
        {wsStatus === 'open'
          ? 'Display connected'
          : wsStatus === 'failed'
            ? 'Live updates unavailable'
            : 'Connecting…'}
      </div>
      <div className="mx-auto flex min-h-screen max-w-4xl flex-col px-6 py-10">
        {mode === 'idle' ? (
          <div className="flex flex-1 flex-col items-center justify-center text-center">
            {storeInfo?.logoUrl ? (
              <img src={storeInfo.logoUrl} alt="" className="mb-6 h-28 w-auto object-contain drop-shadow-lg" />
            ) : null}
            <h1 className="text-4xl font-bold tracking-tight md:text-5xl" style={{ color: '#f8fafc' }}>
              {storeInfo?.storeName ?? 'Welcome'}
            </h1>
            <p className="mt-6 text-2xl font-medium text-slate-200 md:text-3xl">{idleHeadline}</p>

            {storeInfo?.loyaltyEnabled ? (
              <div className="mt-10 w-full max-w-lg rounded-2xl border border-white/20 bg-white/10 p-6 text-left backdrop-blur">
                <p className="text-lg font-semibold text-amber-100">Check your loyalty points</p>
                <label className="mt-3 block text-sm text-slate-200">Phone number</label>
                <div className="mt-1 flex gap-2">
                  <input
                    className="flex-1 rounded-xl border border-white/30 bg-white/90 px-4 py-3 text-lg text-slate-900 outline-none focus:ring-2 focus:ring-amber-300"
                    placeholder="555-123-4567"
                    value={phoneDigits}
                    onChange={(e) => setPhoneDigits(e.target.value)}
                  />
                  <button
                    type="button"
                    className="rounded-xl bg-amber-400 px-5 py-3 text-lg font-bold text-slate-900 disabled:opacity-50"
                    disabled={lookupLoading}
                    onClick={() => void onLoyaltyLookup()}
                  >
                    {lookupLoading ? '…' : 'Check'}
                  </button>
                </div>
                {loyaltyBanner ? <p className="mt-4 text-lg text-white">{loyaltyBanner}</p> : null}
              </div>
            ) : null}

            <div className="mt-14 max-w-xl border-t border-white/20 pt-8 text-slate-300">
              <p className="text-xl font-semibold text-slate-100">We repair all devices</p>
              <p className="mt-2 text-lg">Same-day service available at participating locations.</p>
            </div>
          </div>
        ) : null}

        {mode === 'customer_info' ? (
          <div className="flex flex-1 flex-col">
            <header className="border-b border-white/20 pb-4">
              <p className="text-sm uppercase tracking-widest text-slate-300">Customer entry</p>
              <h1 className="text-3xl font-bold">{storeInfo?.storeName ?? 'Store'}</h1>
            </header>
            <p className="mt-6 text-xl text-slate-200">Please enter your information:</p>
            <div className="mt-6 grid gap-4 md:grid-cols-2">
              <label className="block md:col-span-1">
                <span className="text-sm text-slate-300">First name {required.firstName ? '*' : ''}</span>
                <input
                  className="mt-1 w-full rounded-xl border border-white/20 bg-white/95 px-4 py-3 text-lg text-slate-900"
                  value={customerForm.firstName}
                  onChange={(e) => setCustomerForm((s) => ({ ...s, firstName: e.target.value }))}
                />
              </label>
              <label className="block">
                <span className="text-sm text-slate-300">Last name</span>
                <input
                  className="mt-1 w-full rounded-xl border border-white/20 bg-white/95 px-4 py-3 text-lg text-slate-900"
                  value={customerForm.lastName}
                  onChange={(e) => setCustomerForm((s) => ({ ...s, lastName: e.target.value }))}
                />
              </label>
              <label className="block md:col-span-2">
                <span className="text-sm text-slate-300">Phone {required.phone ? '*' : ''}</span>
                <input
                  className="mt-1 w-full rounded-xl border border-white/20 bg-white/95 px-4 py-3 text-lg text-slate-900"
                  value={customerForm.phone}
                  onChange={(e) => setCustomerForm((s) => ({ ...s, phone: e.target.value }))}
                />
              </label>
              <label className="block md:col-span-2">
                <span className="text-sm text-slate-300">Email</span>
                <input
                  className="mt-1 w-full rounded-xl border border-white/20 bg-white/95 px-4 py-3 text-lg text-slate-900"
                  value={customerForm.email}
                  onChange={(e) => setCustomerForm((s) => ({ ...s, email: e.target.value }))}
                />
              </label>
              {required.address ? (
                <label className="block md:col-span-2">
                  <span className="text-sm text-slate-300">Street address *</span>
                  <input
                    className="mt-1 w-full rounded-xl border border-white/20 bg-white/95 px-4 py-3 text-lg text-slate-900"
                    value={customerForm.address}
                    onChange={(e) => setCustomerForm((s) => ({ ...s, address: e.target.value }))}
                  />
                </label>
              ) : null}
              {required.cityStateZip ? (
                <>
                  <label className="block">
                    <span className="text-sm text-slate-300">City *</span>
                    <input
                      className="mt-1 w-full rounded-xl border border-white/20 bg-white/95 px-4 py-3 text-lg text-slate-900"
                      value={customerForm.city}
                      onChange={(e) => setCustomerForm((s) => ({ ...s, city: e.target.value }))}
                    />
                  </label>
                  <label className="block">
                    <span className="text-sm text-slate-300">State *</span>
                    <input
                      className="mt-1 w-full rounded-xl border border-white/20 bg-white/95 px-4 py-3 text-lg text-slate-900"
                      value={customerForm.state}
                      onChange={(e) => setCustomerForm((s) => ({ ...s, state: e.target.value }))}
                    />
                  </label>
                  <label className="block md:col-span-2">
                    <span className="text-sm text-slate-300">ZIP *</span>
                    <input
                      className="mt-1 w-full rounded-xl border border-white/20 bg-white/95 px-4 py-3 text-lg text-slate-900"
                      value={customerForm.zip}
                      onChange={(e) => setCustomerForm((s) => ({ ...s, zip: e.target.value }))}
                    />
                  </label>
                </>
              ) : null}
            </div>
            <p className="mt-6 text-sm text-slate-400">
              By providing your information you agree to receive service updates from this store.
            </p>
            <div className="mt-8 flex flex-wrap justify-between gap-3">
              <button
                type="button"
                className="rounded-xl border border-white/30 px-6 py-3 font-semibold"
                onClick={() => {
                  setCustomerSession(null);
                  setMode('idle');
                }}
              >
                Clear
              </button>
              <button
                type="button"
                className="rounded-xl bg-emerald-500 px-8 py-3 text-lg font-bold text-slate-950"
                onClick={() => void submitCustomerInfo()}
              >
                Submit information →
              </button>
            </div>
            <p className="mt-6 text-center text-sm text-slate-400">Staff can skip this step on their device.</p>
          </div>
        ) : null}

        {mode === 'review' && review ? (
          isPosCart ? (
            <div className="flex flex-1 flex-col transition-opacity duration-500">
              <style>{`
                @keyframes ff-display-line-in {
                  from { opacity: 0; transform: translateX(28px); }
                  to { opacity: 1; transform: translateX(0); }
                }
                .ff-display-line-in { animation: ff-display-line-in 0.38s ease-out both; }
              `}</style>
              <header className="flex flex-wrap items-baseline justify-between gap-2 border-b border-white/20 pb-4">
                <div>
                  <p className="text-sm uppercase tracking-widest text-slate-300">
                    {String(review.storeName ?? storeInfo?.storeName ?? '')}
                  </p>
                  <h1 className="text-3xl font-bold">Current Sale</h1>
                </div>
                <p className="text-xl text-slate-200">
                  {String(review.customerName ?? '').trim() || 'Walk-in Customer'}
                </p>
              </header>
              <ul className="mt-6 flex-1 space-y-3 overflow-hidden">
                {posLineItems.map((it, idx) => {
                  const lineKey = String(it.id ?? `${it.description}-${idx}`);
                  const qty = Number(it.quantity ?? 1);
                  const unit = Number(it.unitPrice ?? 0);
                  const lineTotal = it.lineTotal != null ? Number(it.lineTotal) : qty * unit;
                  const enter = posLineEnterIds.has(lineKey);
                  return (
                    <li
                      key={lineKey}
                      className={clsx(
                        'flex flex-wrap items-baseline justify-between gap-3 border-b border-white/10 pb-3 text-lg transition-all duration-300',
                        enter && 'ff-display-line-in',
                      )}
                    >
                      <span className="min-w-0 flex-1 font-medium">{it.description}</span>
                      <span className="shrink-0 text-right tabular-nums text-slate-200 transition-colors duration-200">
                        {qty} × {formatCurrency(unit)}{' '}
                        <span className="font-semibold text-white">{formatCurrency(lineTotal)}</span>
                      </span>
                    </li>
                  );
                })}
              </ul>
              <div className="mt-auto space-y-1 border-t border-white/20 pt-4 text-lg">
                <div className="flex justify-between">
                  <span className="text-slate-300">Subtotal</span>
                  <span className="tabular-nums transition-opacity duration-200">
                    {formatCurrency(String(review.subtotal ?? 0))}
                  </span>
                </div>
                <div className="flex justify-between text-rose-200">
                  <span>Discount</span>
                  <span className="tabular-nums transition-opacity duration-200">
                    -{formatCurrency(String(review.discountTotal ?? 0))}
                  </span>
                </div>
                {review.discountReason ? (
                  <p className="text-sm text-rose-100/80">{String(review.discountReason)}</p>
                ) : null}
                {showTax ? (
                  <div className="flex justify-between">
                    <span className="text-slate-300">
                      Tax ({(Number(review.taxRate ?? 0) * 100).toFixed(2)}%)
                    </span>
                    <span className="tabular-nums transition-opacity duration-200">
                      {formatCurrency(String(review.taxTotal ?? 0))}
                    </span>
                  </div>
                ) : null}
                <div className="flex justify-between border-t border-white/20 pt-2 text-2xl font-bold">
                  <span>Total</span>
                  <span className="tabular-nums transition-opacity duration-200">
                    {formatCurrency(String(review.total ?? 0))}
                  </span>
                </div>
              </div>
            </div>
          ) : (
            <div className="flex flex-1 flex-col transition-opacity duration-500">
              {isTicketDraft ? (
                <style>{`
                  @keyframes ff-display-line-in {
                    from { opacity: 0; transform: translateX(28px); }
                    to { opacity: 1; transform: translateX(0); }
                  }
                  .ff-display-line-in { animation: ff-display-line-in 0.38s ease-out both; }
                `}</style>
              ) : null}
              <header className="flex flex-wrap items-baseline justify-between gap-2 border-b border-white/20 pb-4">
                <div>
                  <p className="text-sm uppercase tracking-widest text-slate-300">
                    {String(review.storeName ?? storeInfo?.storeName ?? '')}
                  </p>
                  <h1 className="text-3xl font-bold">
                    {isTicketDraft ? 'Building Repair' : String(review.ticketNumber ?? '')}
                  </h1>
                  {isTicketDraft ? (
                    <p className="mt-1 text-sm text-slate-400">Your estimate is being prepared</p>
                  ) : null}
                </div>
                <p className="text-xl text-slate-200">
                  {String(review.customerName ?? '').trim() || 'Walk-in Customer'}
                </p>
              </header>
              {!showPerDeviceSections &&
              Array.isArray(review.devices) &&
              (review.devices as string[]).length ? (
                <div className="mt-4">
                  <p className="text-xl text-slate-100">{(review.devices as string[]).join(' · ')}</p>
                  <DeviceMetaUnderTitle
                    condition={(review.condition as string | null) ?? deviceGroups[0]?.condition}
                    passcode={(review.passcode as string | null) ?? deviceGroups[0]?.passcode}
                    showCondition={showDeviceCondition}
                    showPin={showDevicePin}
                  />
                </div>
              ) : null}
              {deviceGroups.map((group, groupIdx) => (
                <section key={`${group.label}-${groupIdx}`} className="mt-6">
                  <h2 className="text-sm font-bold uppercase tracking-wide text-amber-200">
                    {showPerDeviceSections ? group.label : 'Services'}
                  </h2>
                  {showPerDeviceSections ? (
                    <DeviceMetaUnderTitle
                      condition={group.condition}
                      passcode={group.passcode}
                      showCondition={showDeviceCondition}
                      showPin={showDevicePin}
                    />
                  ) : null}
                  <ul className="mt-2 space-y-2">
                    {group.services.map((s, idx) => {
                      const lineKey = String(s.id ?? `${groupIdx}-${s.name}-${idx}`);
                      return (
                        <li
                          key={lineKey}
                          className={clsx(
                            'flex justify-between text-lg transition-all duration-300',
                            isTicketDraft && posLineEnterIds.has(lineKey) && 'ff-display-line-in',
                          )}
                        >
                          <span>{s.name}</span>
                          <span className="font-semibold tabular-nums">{formatCurrency(s.price)}</span>
                        </li>
                      );
                    })}
                  </ul>
                </section>
              ))}
              {items.length > 0 ? (
              <section className="mt-4">
                <h2 className="text-sm font-bold uppercase tracking-wide text-cyan-200">Items</h2>
                <ul className="mt-2 space-y-2">
                  {items.map((it) => (
                    <li key={it.description} className="flex justify-between text-lg">
                      <span>{it.description}</span>
                      <span>{formatCurrency(it.lineTotal)}</span>
                    </li>
                  ))}
                </ul>
              </section>
              ) : null}
              <div className="mt-8 space-y-1 border-t border-white/20 pt-4 text-lg">
                <div className="flex justify-between">
                  <span className="text-slate-300">Subtotal</span>
                  <span>{formatCurrency(String(review.subtotal ?? 0))}</span>
                </div>
                <div className="flex justify-between text-rose-200">
                  <span>Discount</span>
                  <span>-{formatCurrency(String(review.discountTotal ?? 0))}</span>
                </div>
                {showTax ? (
                  <div className="flex justify-between">
                    <span className="text-slate-300">Tax</span>
                    <span>{formatCurrency(String(review.taxTotal ?? 0))}</span>
                  </div>
                ) : null}
                <div className="flex justify-between border-t border-white/20 pt-2 text-2xl font-bold">
                  <span>Total</span>
                  <span>{formatCurrency(String(review.total ?? 0))}</span>
                </div>
                {review.promisedDate ? (
                  <p className="pt-4 text-center text-slate-300">
                    Estimated ready:{' '}
                    {new Date(String(review.promisedDate)).toLocaleString(undefined, {
                      weekday: 'short',
                      hour: 'numeric',
                      minute: '2-digit',
                    })}
                  </p>
                ) : null}
              </div>
            </div>
          )
        ) : null}

        {mode === 'waiver' && lastPayload ? (
          <div className="flex flex-1 flex-col">
            <header className="border-b border-white/20 pb-4">
              <p className="text-sm uppercase tracking-widest text-slate-300">
                {lastPayload.waiverType === 'post_repair' ? 'Post-repair' : 'Pre-repair'} authorization
              </p>
              <h1 className="text-2xl font-bold">{String(lastPayload.storeName ?? storeInfo?.storeName ?? '')}</h1>
            </header>
            <p className="mt-4 text-lg">Customer: {String(lastPayload.customerName ?? '')}</p>
            <p className="text-lg">Device: {String(lastPayload.device ?? '')}</p>
            <p className="text-lg">
              Service: {String(lastPayload.service ?? '')} — {String(lastPayload.price ?? '')}
            </p>
            <div
              ref={waiverScrollRef}
              onScroll={checkWaiverScroll}
              className="mt-4 max-h-[40vh] overflow-y-auto rounded-2xl border border-white/20 bg-white/95 p-4 text-left text-base leading-relaxed text-slate-900"
            >
              {String(lastPayload.content ?? '')}
            </div>
            <p className="mt-2 text-center text-sm text-amber-200">Scroll down to read the full agreement, then sign.</p>
            <p className="mt-6 font-semibold text-slate-200">Your signature</p>
            <canvas
              ref={canvasRef}
              width={900}
              height={220}
              className="mt-2 w-full touch-none rounded-2xl border-2 border-white/40 bg-white"
              style={{ maxHeight: 200 }}
              onMouseDown={startDraw}
              onMouseMove={draw}
              onMouseUp={endDraw}
              onMouseLeave={endDraw}
              onTouchStart={(e) => {
                e.preventDefault();
                startDraw(e);
              }}
              onTouchMove={(e) => {
                e.preventDefault();
                draw(e);
              }}
              onTouchEnd={(e) => {
                e.preventDefault();
                endDraw();
              }}
            />
            <button type="button" className="mt-2 text-left text-sm font-semibold text-amber-200" onClick={clearSignature}>
              Clear signature
            </button>
            <button
              type="button"
              disabled={!waiverScrollOk || !hasSignature}
              className={clsx(
                'mt-6 rounded-2xl px-6 py-4 text-lg font-bold',
                waiverScrollOk && hasSignature ? 'bg-emerald-500 text-slate-900' : 'bg-slate-600 text-slate-300',
              )}
              onClick={() => void submitWaiver()}
            >
              Submit signature
            </button>
          </div>
        ) : null}

        {mode === 'form' && lastPayload ? (
          <div className="flex flex-1 flex-col">
            <header className="border-b border-white/20 pb-4">
              <p className="text-sm uppercase tracking-widest text-slate-300">Please complete</p>
              <h1 className="text-2xl font-bold">{String(lastPayload.formName ?? 'Form')}</h1>
              {lastPayload.description ? (
                <p className="mt-2 text-slate-300">{String(lastPayload.description)}</p>
              ) : null}
            </header>
            <div className="mt-6 max-h-[70vh] space-y-5 overflow-y-auto pr-1">
              {((lastPayload.fields as DisplayFormField[]) ?? [])
                .slice()
                .sort((a, b) => a.order - b.order)
                .map((field) => {
                  if (field.type === 'heading') {
                    return (
                      <p key={field.id} className="text-xl font-bold text-white">
                        {field.label}
                      </p>
                    );
                  }
                  if (field.type === 'divider') {
                    return <hr key={field.id} className="border-white/20" />;
                  }
                  if (field.type === 'signature') {
                    return (
                      <div key={field.id}>
                        <p className="mb-2 font-semibold text-slate-100">
                          {field.label}
                          {field.required ? ' *' : ''}
                        </p>
                        <div className="rounded-2xl bg-white p-2">
                          <SignaturePad
                            height={160}
                            helpText={field.helpText ?? 'Sign above'}
                            onChange={(dataUrl) => {
                              setFormValues((prev) => ({
                                ...prev,
                                [field.id]: dataUrl
                                  ? buildFormSignatureValue({ dataUrl })
                                  : null,
                              }));
                            }}
                          />
                        </div>
                      </div>
                    );
                  }
                  if (field.type === 'yes_no') {
                    return (
                      <fieldset key={field.id} className="text-slate-100">
                        <legend className="font-semibold">
                          {field.label}
                          {field.required ? ' *' : ''}
                        </legend>
                        <div className="mt-2 flex gap-4">
                          {(['yes', 'no'] as const).map((opt) => (
                            <label key={opt} className="flex items-center gap-2 capitalize">
                              <input
                                type="radio"
                                name={field.id}
                                checked={formValues[field.id] === opt}
                                onChange={() =>
                                  setFormValues((prev) => ({ ...prev, [field.id]: opt }))
                                }
                              />
                              {opt}
                            </label>
                          ))}
                        </div>
                      </fieldset>
                    );
                  }
                  if (field.type === 'textarea') {
                    return (
                      <label key={field.id} className="block text-slate-100">
                        <span className="font-semibold">
                          {field.label}
                          {field.required ? ' *' : ''}
                        </span>
                        <textarea
                          className="mt-2 w-full rounded-xl border border-white/30 bg-white px-4 py-3 text-slate-900"
                          rows={3}
                          value={String(formValues[field.id] ?? '')}
                          onChange={(e) =>
                            setFormValues((prev) => ({ ...prev, [field.id]: e.target.value }))
                          }
                        />
                      </label>
                    );
                  }
                  return (
                    <label key={field.id} className="block text-slate-100">
                      <span className="font-semibold">
                        {field.label}
                        {field.required ? ' *' : ''}
                      </span>
                      <input
                        className="mt-2 w-full rounded-xl border border-white/30 bg-white px-4 py-3 text-lg text-slate-900"
                        value={String(formValues[field.id] ?? '')}
                        onChange={(e) =>
                          setFormValues((prev) => ({ ...prev, [field.id]: e.target.value }))
                        }
                      />
                    </label>
                  );
                })}
            </div>
            {lastPayload.formError ? (
              <p className="mt-4 text-center text-lg text-amber-200">{String(lastPayload.formError)}</p>
            ) : null}
            <button
              type="button"
              disabled={formSubmitting}
              className="mt-6 rounded-2xl bg-emerald-500 px-6 py-4 text-lg font-bold text-slate-900 disabled:opacity-60"
              onClick={() => void submitDisplayForm()}
            >
              {formSubmitting ? 'Submitting…' : 'Submit form'}
            </button>
          </div>
        ) : null}

        {(mode === 'checkout' || mode === 'payment_processing') && lastPayload ? (
          <div className="flex flex-1 flex-col items-stretch">
            <h1 className="text-2xl font-bold">{String(lastPayload.storeName ?? storeInfo?.storeName ?? '')}</h1>
            <p className="mt-6 text-center text-3xl font-bold uppercase tracking-wide text-slate-200">Order total</p>
            <ul className="mt-6 space-y-2 text-lg">
              {((lastPayload.lines as { description: string; lineTotal: string }[]) ?? []).map((l) => (
                <li key={l.description} className="flex justify-between">
                  <span>{l.description}</span>
                  <span>{formatCurrency(l.lineTotal)}</span>
                </li>
              ))}
            </ul>
            <div className="mt-6 space-y-1 border-t border-white/20 pt-4 text-lg">
              <div className="flex justify-between">
                <span>Subtotal</span>
                <span>{formatCurrency(String(lastPayload.subtotal ?? 0))}</span>
              </div>
              <div className="flex justify-between text-rose-200">
                <span>Discount</span>
                <span>-{formatCurrency(String(lastPayload.discountTotal ?? 0))}</span>
              </div>
              <div className="flex justify-between">
                <span>Tax</span>
                <span>{formatCurrency(String(lastPayload.taxTotal ?? 0))}</span>
              </div>
              <div className="flex justify-between text-2xl font-bold">
                <span>Total</span>
                <span>{formatCurrency(String(lastPayload.total ?? 0))}</span>
              </div>
              {lastPayload.storeCreditApplied ? (
                <div className="flex justify-between text-emerald-200">
                  <span>Store credit</span>
                  <span>-{formatCurrency(String(lastPayload.storeCreditApplied))}</span>
                </div>
              ) : null}
              <div className="flex justify-between border-t border-white/30 pt-2 text-xl font-semibold">
                <span>Amount due</span>
                <span>{formatCurrency(String(lastPayload.amountDue ?? lastPayload.total ?? 0))}</span>
              </div>
            </div>
            {mode === 'payment_processing' ? (
              <p className="mt-10 text-center text-2xl font-semibold text-amber-200">Processing payment…</p>
            ) : null}
          </div>
        ) : null}

        {mode === 'thank_you' && lastPayload ? (
          <div className="flex flex-1 flex-col items-center justify-center text-center">
            <div className="text-6xl text-emerald-400">✓</div>
            <h1 className="mt-6 text-4xl font-bold">Thank you,</h1>
            <p className="text-3xl font-semibold text-slate-100">{String(lastPayload.customerFirstName ?? '')}</p>
            <p className="mt-6 text-2xl">Total paid: {formatCurrency(String(lastPayload.totalPaid ?? 0))}</p>
            {Number(lastPayload.pointsEarned ?? 0) > 0 ? (
              <div className="mt-8 rounded-2xl border border-amber-300/40 bg-amber-500/10 px-6 py-4 text-lg">
                <p>
                  You earned {String(lastPayload.pointsEarned)} points today
                  {lastPayload.loyaltyBalance != null ? (
                    <>
                      . New balance: {String(lastPayload.loyaltyBalance)} points (≈ $
                      {formatMoneyFromPoints(Number(lastPayload.loyaltyBalance))} in rewards).
                    </>
                  ) : (
                    '!'
                  )}
                </p>
              </div>
            ) : null}
            {lastPayload.receiptEmail ? (
              <p className="mt-8 text-lg text-slate-200">
                Receipt sent to: <span className="font-semibold text-white">{String(lastPayload.receiptEmail)}</span>
              </p>
            ) : null}
          </div>
        ) : null}

        {lastPayload && typeof lastPayload.thankYouNote === 'string' ? (
          <div className="fixed inset-x-0 bottom-8 flex justify-center px-4">
            <div className="rounded-2xl bg-emerald-600 px-6 py-4 text-center text-lg font-semibold shadow-xl">
              {lastPayload.thankYouNote}
            </div>
          </div>
        ) : null}
      </div>
    </main>
  );
}
